import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { ReactComponent as Preview } from "../../../../../assets/icons/svg/Preview_Playlist.svg";

interface CreativeWithDuration {
  id: string;
  title: string;
  imageUrl: string;
  duration?: number;
  fileFormat?: string;
  orientation?: "landscape" | "portrait";
  [key: string]: any;
}

interface PlaylistPreviewModalProps {
  isOpen: boolean;
  creatives: CreativeWithDuration[];
  onClose: () => void;
  creativeDurations?: Record<string, number>;
}

const PlaylistPreviewModal: React.FC<PlaylistPreviewModalProps> = ({
  isOpen,
  creatives,
  onClose,
  creativeDurations = {},
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [progress, setProgress] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);

  const currentCreative = creatives[currentIndex];
  const currentDuration = currentCreative 
    ? (creativeDurations[currentCreative.id] || currentCreative.duration || 10)
    : 10;

  // Check for mobile view
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 768); // md breakpoint
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Reset image error and progress when creative changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setImageError(false);
      setProgress(0);
      setStartTime(Date.now());
    }
  }, [currentIndex, isOpen]);

  // Auto-play functionality with dynamic progress
  useEffect(() => {
    if (!isOpen || creatives.length === 0) return;

    const interval = setInterval(() => {
      const now = Date.now();
      if (startTime) {
        const elapsed = (now - startTime) / 1000; // Convert to seconds
        const currentProgress = (elapsed / currentDuration) * 100;

        if (currentProgress >= 100) {
          // Move to next item
          const nextIndex = currentIndex < creatives.length - 1 ? currentIndex + 1 : 0;
          setCurrentIndex(nextIndex);
        } else {
          setProgress(currentProgress);
        }
      }
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(interval);
  }, [isOpen, currentIndex, currentDuration, startTime, creatives.length]);

  // Navigation handlers - immediate navigation on button click
  const handlePrevious = useCallback(() => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : creatives.length - 1;
    setCurrentIndex(newIndex);
  }, [currentIndex, creatives.length]);

  const handleNext = useCallback(() => {
    const newIndex = currentIndex < creatives.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
  }, [currentIndex, creatives.length]);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const { naturalWidth, naturalHeight } = img;

    // Calculate optimal size for the preview image based on screen size
    const isSmallScreen = window.innerWidth < 768;
    const isMediumScreen = window.innerWidth < 1024;
    
    let maxWidth = 600;
    let maxHeight = 400;
    
    if (isSmallScreen) {
      maxWidth = Math.min(300, window.innerWidth - 32); // Account for padding
      maxHeight = Math.min(200, window.innerHeight * 0.3);
    } else if (isMediumScreen) {
      maxWidth = 400;
      maxHeight = 300;
    }

    let width = naturalWidth;
    let height = naturalHeight;
    const aspectRatio = width / height;

    if (width > maxWidth || height > maxHeight) {
      if (width / aspectRatio <= maxHeight) {
        width = maxWidth;
        height = width / aspectRatio;
      } else {
        height = maxHeight;
        width = height * aspectRatio;
      }
    }

    setImageSize({ width, height });
  };

  const handleImageError = () => setImageError(true);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen || creatives.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4 overflow-auto">
      <div className="bg-white rounded-lg sm:rounded-xl shadow-xl relative my-2 sm:my-12 w-full h-full sm:h-auto sm:max-w-6xl sm:max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Preview</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close preview"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content - Mobile: Stack vertically, Desktop: Side by side */}
        <div className={`flex ${isMobileView ? 'flex-col' : 'flex-row'}`}>
          {/* Left Section - Preview */}
          <div className={`flex-1 flex flex-col min-w-0 ${isMobileView ? 'order-1' : ''}`}>
            {/* Main Image Display */}
            <div className={`flex-1 flex items-center justify-center bg-white p-3 sm:p-4 min-h-0 ${isMobileView ? 'min-h-[200px] sm:min-h-[250px]' : ''}`}>
              <div className="flex justify-center items-center border-[4px] border-black rounded-lg ">
                {!imageError && currentCreative ? (
                  <img
                    src={currentCreative.imageUrl}
                    alt={currentCreative.title}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    className="rounded object-contain max-w-full max-h-full"
                    style={{
                      width: imageSize.width ? `${imageSize.width}px` : 'auto',
                      height: imageSize.height ? `${imageSize.height}px` : 'auto',
                      maxWidth: isMobileView ? '280px' : '600px',
                      maxHeight: isMobileView ? '180px' : '400px',
                    }}
                  />
                ) : (
                  <div className={`flex flex-col items-center justify-center text-gray-400 p-4 sm:p-8 bg-gray-100 rounded min-h-[120px] sm:min-h-[200px] ${isMobileView ? 'w-[200px] sm:w-[280px]' : 'w-[300px]'}`}>
                    <svg
                      className={`${isMobileView ? 'w-8 h-8 sm:w-12 sm:h-12' : 'w-16 h-16'} mb-2 sm:mb-4`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-sm sm:text-lg">Preview not available</span>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="px-3 sm:px-6 py-3 sm:py-4 bg-white border-t sm:border-t-0">
              <div className="flex items-center justify-between">
                {/* Previous Button */}
                <button
                  onClick={handlePrevious}
                  className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full hover:bg-gray-200 transition-colors"
                  disabled={creatives.length <= 1}
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                </button>

                {/* Current Image Info */}
                <div className="text-center px-2 sm:px-4 flex-1 min-w-0">
                  <div className="text-sm sm:text-base font-medium text-gray-900 truncate">
                    {currentCreative?.title}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">
                    {currentIndex + 1} of {creatives.length}
                  </div>
                </div>

                {/* Next Button */}
                <button
                  onClick={handleNext}
                  className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full hover:bg-gray-200 transition-colors"
                  disabled={creatives.length <= 1}
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                </button>
              </div>
            </div>
          </div>

          {/* Right Section - Now Playing */}
          <div className={`${isMobileView ? 'order-2 border-t' : 'w-64 sm:w-72 lg:w-80 border-l'} bg-white border-gray-200 flex flex-col ${isMobileView ? 'flex-1 min-h-0' : 'my-0 sm:my-4'}`}>
            {/* Sidebar Header */}
            <div className="px-3 sm:px-4 py-2 sm:py-3">
              <h3 className="text-sm sm:text-base font-medium text-gray-900">Now playing</h3>
            </div>

            {/* Playlist Items */}
            <div className={`flex-1 overflow-y-auto ${isMobileView ? 'max-h-[200px] sm:max-h-[250px]' : ''}`}>
              {creatives.map((creative, index) => {
                const duration = creativeDurations[creative.id] || creative.duration || 10;
                const isActive = index === currentIndex;

                return (
                  <div
                    key={creative.id}
                    className={`cursor-pointer transition-colors relative overflow-hidden  ${
                      isActive ? " bg-gray-200 " : "border-transparent hover:bg-gray-50"
                    }`}
                    onClick={() => setCurrentIndex(index)}
                  >
                    {/* Background progress bar for active item */}
                    {isActive && (
                      <div 
                        className="absolute inset-0 transition-all duration-100 ease-linear"
                        style={{ 
                          width: `${Math.min(100, Math.max(0, progress))}%`,
                          background: 'linear-gradient(90deg, #E8FAFF -6.77%, #9EC9D7 142.97%)',
                          transform: 'translateZ(0)' // Force GPU acceleration for smoother animation
                        }}
                      />
                    )}
                    
                    {/* Main content area */}
                    <div className="flex items-center px-3 sm:px-4 py-2 sm:py-3 relative z-10">
                      
                      {/* Preview Icon - Only show for active item */}
                      {isActive && (
                        <Preview className="w-2 h-2 mr-2" />
                      )}

                      {/* Creative Thumbnail */}
                      <div className={`${isMobileView ? 'w-10 h-7 sm:w-12 sm:h-8' : 'w-16 h-10'} rounded overflow-hidden flex-shrink-0 mr-2 sm:mr-3 bg-gray-200`}>
                        <img
                          src={creative.imageUrl}
                          alt={creative.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const placeholder = target.nextElementSibling as HTMLElement;
                            if (placeholder) {
                              placeholder.classList.remove('hidden');
                            }
                          }}
                        />
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center hidden">
                          <svg
                            className={`${isMobileView ? 'w-3 h-3' : 'w-6 h-6'} text-gray-400`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Creative Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 
                            className={`text-xs sm:text-sm font-medium truncate pr-2 ${
                              isActive ? "text-blue-900" : "text-gray-900"
                            }`}
                            title={creative.title}
                          >
                            {creative.title}
                          </h4>
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          {duration} sec
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaylistPreviewModal;